import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { MERCHANT_ADMIN_PRODUCTS_GROUP_ROUTE, MERCHANT_ADMIN_ROUTE } from "../../../../../constants/routes"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../shared/components/links/StandardBreadcrumbs"
import { PageHeader } from "../../../../../shared/components/pageHeader/PageHeader"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { faInfoCircle } from "@fortawesome/pro-light-svg-icons"
import { Page } from "../../../../../shared/components/pageHeader/PageHeaderApi"
import type { CreateProductGroup, ProductGroupFormValues } from "../productGroupTypes"
import {
    GetProductGroupDetailsResponse,
    getProductGroupDetails,
    updateProductGroup,
    ProductGroupQuery,
} from "../productGroupApi"
import { useApiMutation } from "../../../../../core/api/useApiMutation"
import { ErrorDisplay, SuccessDisplay } from "../../../../../core/api/errorTypes"
import { type FetchProductsResponse, MerchantProductsQuery, fetchProducts } from "../../products/productsApi"
import type { ProductsTableItem } from "../../products/productsTypes"
import { type NarrowedAvailability, narrowToProductGroup } from "../../products/productGroupAvailability"
import { useNarrowProductAvailability } from "../../products/hooks/useNarrowProductAvailability"

import { useNavigate, useParams } from "react-router-dom"
import { queryKeyFor, useApiQuery } from "../../../../../core/api/useApiQuery"
import { useDateTime } from "../../../../localization/useDateTime"
import { useMerchant } from "../../../../merchants/merchantHooks"
import { prepareProductGroup } from "../helpers/prepareProductGroup"
import { prepareInitialValues } from "../helpers/prepareInitialValues"
import { StandardButton } from "../../../../../shared/components/buttons/StandardButton"
import React, { forwardRef, Suspense, useMemo, useRef, useState } from "react"
import { FormikProps } from "formik"
import { QueryErrorBoundary } from "../../../../../core/api/components/QueryErrorBoundary"
import { LoadingSpinner } from "../../../../../shared/components/loading/LoadingSpinner"
import { CreateUpdateProductGroupForm } from "../components/CreateUpdateProductGroupForm"
import { ProductsOutsideGroupAvailabilityDialog } from "../components/ProductsOutsideGroupAvailabilityDialog"

function transformData(data: GetProductGroupDetailsResponse) {
    return data.productGroup
}

const selectGroupProducts = (data: FetchProductsResponse, productGroupId: number) =>
    data.productGroups.find((group) => group.id === productGroupId)?.products ?? []

type ProductToNarrow = Readonly<{
    product: ProductsTableItem
    narrowedAvailability: NarrowedAvailability
}>

type PendingProductGroupUpdate = Readonly<{
    productGroup: CreateProductGroup
    products: ReadonlyArray<ProductToNarrow>
}>

// failures are already shown by the mutations themselves
const ignoreApiError = () => undefined

export function EditProductGroup() {
    const formRef = useRef<FormikProps<ProductGroupFormValues>>(null)

    const handleSaveClick = () => {
        formRef.current?.submitForm()
    }

    return (
        <>
            <LocalizedStrict id="product-group-page-title-update" attrs={{ title: true }}>
                <PageHeader
                    title="Edit product group"
                    page={Page.MERCHANT_PRODUCT_GROUP}
                    icon={<FontAwesomeIcon icon={faInfoCircle} />}
                    rightElement={
                        <LocalizedStrict id="create-product-group-save-button">
                            <StandardButton onClick={handleSaveClick}>Save</StandardButton>
                        </LocalizedStrict>
                    }
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="merchant-nav-item-home">
                            <BreadcrumbLink to={MERCHANT_ADMIN_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="merchant-nav-item-products-header">
                            <Breadcrumb>Products</Breadcrumb>
                        </LocalizedStrict>
                        <LocalizedStrict id="merchant-nav-item-product-groups">
                            <BreadcrumbLink to={MERCHANT_ADMIN_PRODUCTS_GROUP_ROUTE}>Product groups</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="merchant-nav-item-product-group-update">
                            <Breadcrumb>Update product group</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>

            <QueryErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                    <ProductGroup ref={formRef} />
                </Suspense>
            </QueryErrorBoundary>
        </>
    )
}

const ProductGroup = forwardRef<FormikProps<ProductGroupFormValues>>((props, ref) => {
    const navigate = useNavigate()

    const { merchantId } = useMerchant()
    const dateTimeFactory = useDateTime()

    const { productGroupId } = useParams()
    const productGroupIdNumber = Number(productGroupId)

    const { mutateAsync: callUpdateProductGroup } = useApiMutation(updateProductGroup, {
        successDisplay: SuccessDisplay.SNACKBAR,
        async onSuccess(params, __, context) {
            await context.queryClient.invalidateQueries({ queryKey: [ProductGroupQuery.LIST] })
            // the products list carries the group's availability too
            await context.queryClient.invalidateQueries({ queryKey: [MerchantProductsQuery.LIST] })
            await context.queryClient.invalidateQueries({
                queryKey: queryKeyFor(ProductGroupQuery.DETAILS, {
                    merchantId: params.merchantId,
                    productGroupId: params.productGroupId,
                }),
            })
        },
    })
    const narrowProductAvailability = useNarrowProductAvailability()

    const { data: productGroup } = useApiQuery(getProductGroupDetails, {
        queryName: ProductGroupQuery.DETAILS,
        dependencies: {
            merchantId,
            productGroupId: productGroupIdNumber ?? 0,
        },
        suspense: true,
        useErrorBoundary: true,
        enabled: productGroupId !== undefined,
        select: transformData,
    })

    const priceListId = productGroup?.priceLists?.[0]?.id
    // same dependencies as the products tab, so its cached list is reused
    const deliveryDate = useMemo(() => dateTimeFactory.toDate(dateTimeFactory.now()), [dateTimeFactory])

    const groupProducts = useApiQuery(fetchProducts, {
        queryName: MerchantProductsQuery.LIST,
        dependencies: { merchantId, priceListId, deliveryDate },
        enabled: priceListId !== undefined,
        select: (data) => selectGroupProducts(data, productGroupIdNumber),
        errorDisplay: ErrorDisplay.NONE,
    })

    const [pendingUpdate, setPendingUpdate] = useState<PendingProductGroupUpdate | null>(null)
    const [isSavingPendingUpdate, setIsSavingPendingUpdate] = useState(false)

    async function saveProductGroup({ productGroup, products }: PendingProductGroupUpdate) {
        await callUpdateProductGroup({
            merchantId,
            productGroupId: productGroupIdNumber,
            productGroup,
        })
        if (products.length) {
            await narrowProductAvailability(
                products.map(({ product, narrowedAvailability }) => ({ productId: product.id, narrowedAvailability })),
            )
        }
        navigate(MERCHANT_ADMIN_PRODUCTS_GROUP_ROUTE)
    }

    // the list may still be loading when the form is submitted
    async function loadGroupProducts() {
        if (groupProducts.data || priceListId === undefined) return groupProducts.data ?? []
        const { data } = await groupProducts.refetch()
        return data ?? []
    }

    async function handleEditProductGroup(values: ProductGroupFormValues) {
        const productGroup = prepareProductGroup(values, dateTimeFactory)
        const productsToNarrow = (await loadGroupProducts()).flatMap((product) => {
            const narrowedAvailability = narrowToProductGroup(
                product.availabilityDetails,
                productGroup.availabilityDetails,
                dateTimeFactory,
            )
            return narrowedAvailability ? [{ product, narrowedAvailability }] : []
        })

        if (productsToNarrow.length) {
            setPendingUpdate({ productGroup, products: productsToNarrow })
            return
        }

        await saveProductGroup({ productGroup, products: [] }).catch(ignoreApiError)
    }

    async function confirmPendingUpdate(update: PendingProductGroupUpdate) {
        setIsSavingPendingUpdate(true)
        await saveProductGroup(update).catch(ignoreApiError)
        setIsSavingPendingUpdate(false)
        setPendingUpdate(null)
    }

    return (
        <>
            <CreateUpdateProductGroupForm
                ref={ref}
                history={productGroup?.history}
                initialValues={prepareInitialValues(dateTimeFactory, productGroup)}
                onSubmit={handleEditProductGroup}
            />
            {pendingUpdate && (
                <ProductsOutsideGroupAvailabilityDialog
                    products={pendingUpdate.products.map(({ product, narrowedAvailability }) => ({
                        id: product.id,
                        name: product.name,
                        hasNoOverlap: narrowedAvailability.hasNoOverlap,
                    }))}
                    isSaving={isSavingPendingUpdate}
                    onConfirm={() => {
                        confirmPendingUpdate(pendingUpdate)
                    }}
                    onCancel={() => {
                        if (!isSavingPendingUpdate) setPendingUpdate(null)
                    }}
                />
            )}
        </>
    )
})
